import React from 'react'
import { t } from 'ttag'
import styles from './TalentAreasTable.module.scss'
import cx from 'classnames'
import arrowUp from 'assets/icons/arrow-up.svg'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Icon,
  RefreshCwIcon,
} from 'components/shared/Icon/Icon'
import { Button } from 'components/shared/Button/Button'
import { FilterDropdown } from '../Filter/FilterDropdown'
import {
  getColumns,
  getDefaultSortDirection,
  VALUE_LEVEL_COLUMN,
} from './model'
import { capitalizeFirstLetter } from 'utils/stringFormat'
import { useLanguage } from 'hooks'
import { valueLevel } from 'components/shared/ScatterPlot/ScatterPlot'
import { TalentAreaDetailModal } from './TalentAreaDetailModal'

const Sorting = ({ isDescending, setAscending, setDescending }) => {
  const suppressFocusScroll = e => {
    if (e.button === 0) {
      e.preventDefault()
    }
  }

  const isNeutral = isDescending === null

  if (isNeutral) {
    return (
      <div className={styles.sorting}>
        <button
          type="button"
          className={styles.sortButtonNeutral}
          onMouseDown={suppressFocusScroll}
          onClick={setAscending}
        >
          <Icon icon={arrowUp} size={14} className={styles.sortIconNeutral} />
        </button>
        <button
          type="button"
          className={styles.sortButtonNeutral}
          onMouseDown={suppressFocusScroll}
          onClick={setDescending}
        >
          <Icon
            icon={arrowUp}
            size={14}
            className={cx(styles.sortIconNeutral, styles.sortIconNeutralDown)}
          />
        </button>
      </div>
    )
  }

  const isAscending = isDescending === false

  return (
    <div className={styles.sorting}>
      <button
        type="button"
        className={styles.sortButtonActive}
        onMouseDown={suppressFocusScroll}
        onClick={isAscending ? setDescending : setAscending}
      >
        <Icon
          icon={isAscending ? ArrowUpIcon : ArrowDownIcon}
          size={14}
          className={styles.sortIconActive}
        />
      </button>
    </div>
  )
}

const ProgressBar = ({ score, isMediumValue, isLowValue }) => {
  return (
    <div className={styles.progressContainer}>
      <div
        className={cx(styles.progressBar, {
          [styles.medium]: isMediumValue,
          [styles.low]: isLowValue,
        })}
        style={{ width: `${score}%` }}
      ></div>
    </div>
  )
}

export const TalentAreasTable = ({
  data,
  contributionSignificance,
  setOrder,
  ordering,
  categoriesFilter,
  valueFilter,
  updateValueFilter,
  updateCategoryFilter,
  scrollContainerRef,
}) => {
  const [areaDetail, setAreaDetail] = React.useState(null)
  const scrollRestoreRef = React.useRef(null)

  const closeAreaDetail = () => setAreaDetail(null)

  const openAreaDetail = row => setAreaDetail(row)

  const language = useLanguage()

  /** Saves the scroll position, runs a new column sort, then layout effect restores that position. */
  const setOrderPreservingScroll = React.useCallback(
    (direction, field) => {
      const scrollEl = scrollContainerRef?.current
      if (scrollEl) {
        scrollRestoreRef.current = {
          element: scrollEl,
          top: scrollEl.scrollTop,
        }
      }

      setOrder(direction, field)
    },
    [setOrder, scrollContainerRef],
  )

  /** After rows re-order, restores scroll on the modal panel so the view does not jump. */
  React.useLayoutEffect(() => {
    const payload = scrollRestoreRef.current
    if (!payload) {
      return
    }

    scrollRestoreRef.current = null
    const { element, top } = payload

    if (element.isConnected) {
      element.scrollTop = top
    }
  }, [ordering])

  const resetSorting = React.useCallback(() => {
    setOrderPreservingScroll('descending', VALUE_LEVEL_COLUMN)
  }, [setOrderPreservingScroll])

  const handleSortClick = React.useCallback(
    (field, direction) => {
      const activeField = ordering.descending ?? ordering.ascending
      const sortDirection =
        activeField === field ? direction : getDefaultSortDirection(field)

      setOrderPreservingScroll(sortDirection, field)
    },
    [ordering, setOrderPreservingScroll],
  )

  return (
    <>
      <div className={styles.tableContainer}>
        <div className={styles.title}>
          {t`Talent areas`}
          <div className={styles.titleActions}>
            <Button
              className={styles.resetSortButton}
              icon={RefreshCwIcon}
              label={t`Reset sorting`}
              size="small"
              variant="outlined"
              onClick={resetSorting}
            />
            <FilterDropdown
              categoriesFilter={categoriesFilter}
              valueFilter={valueFilter}
              updateValueFilter={updateValueFilter}
              updateCategoryFilter={updateCategoryFilter}
            />
          </div>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              {getColumns().map(({ header, field }) => (
                <th scope="col" key={header} className={styles.header}>
                  <div className={styles.headerContent}>
                    <span className={styles.headerLabel}>{header}</span>
                    <Sorting
                      setAscending={() => handleSortClick(field, 'ascending')}
                      setDescending={() => handleSortClick(field, 'descending')}
                      isDescending={
                        ordering.descending === field
                          ? true
                          : ordering.ascending === field
                            ? false
                            : null
                      }
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(row => {
              const {
                area,
                personalityScore,
                interestScore,
                matchScore,
                category,
              } = row
              const level = valueLevel(matchScore)
              const isHighValue = level === 'high'
              const isMediumValue = level === 'medium'
              const isLowValue = level === 'low'

              return (
                <tr
                  key={area}
                  className={cx(styles.row, {
                    [styles.medium]: isMediumValue,
                    [styles.low]: isLowValue,
                  })}
                >
                  <td>
                    <div
                      className={styles.area}
                      role="button"
                      tabIndex={0}
                      onClick={() => openAreaDetail(row)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openAreaDetail(row)
                        }
                      }}
                    >
                      {area}
                    </div>
                  </td>
                  <td>
                    <div className={styles.valueLevel}>
                      <div className={styles.valueLevelScore}>{matchScore}</div>
                      <div
                        className={cx(styles.message, {
                          [styles.medium]: isMediumValue,
                          [styles.low]: isLowValue,
                        })}
                      >
                        {isHighValue
                          ? t`High match`
                          : isMediumValue
                            ? t`Medium match`
                            : t`Low match`}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.personality}>
                      {personalityScore}{' '}
                      <ProgressBar
                        score={personalityScore}
                        isMediumValue={isMediumValue}
                        isLowValue={isLowValue}
                      />
                    </div>
                  </td>
                  <td>
                    <div className={styles.personality}>
                      {interestScore}{' '}
                      <ProgressBar
                        score={interestScore}
                        isMediumValue={isMediumValue}
                        isLowValue={isLowValue}
                      />
                    </div>
                  </td>
                  <td>
                    <div className={styles.category}>
                      {capitalizeFirstLetter(category)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <TalentAreaDetailModal
        areaRow={areaDetail}
        contributionSignificance={contributionSignificance}
        language={language}
        onClose={closeAreaDetail}
      />
    </>
  )
}
